import { useEffect } from "react";

type HotkeyHandler = (event: KeyboardEvent) => void;

interface HotkeyMap {
  [key: string]: HotkeyHandler;
}

/**
 * Bind keyboard shortcuts. Keys are case-insensitive single characters
 * or special tokens like "Escape", "/", "ArrowDown".
 *
 * Modifiers: prefix with "mod+" for Cmd/Ctrl (e.g. "mod+k").
 *
 * Skips events when the user is typing inside input/textarea/contenteditable.
 */
export function useHotkeys(map: HotkeyMap, deps: unknown[] = []) {
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const isTyping =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable;

      const mod = e.metaKey || e.ctrlKey;

      for (const [combo, handler] of Object.entries(map)) {
        const wantsMod = combo.startsWith("mod+");
        const key = wantsMod ? combo.slice(4) : combo;

        const matches =
          e.key.toLowerCase() === key.toLowerCase() &&
          (wantsMod ? mod : !mod);

        // Allow mod+key shortcuts even when typing (like cmd+k)
        if (matches && (!isTyping || wantsMod)) {
          e.preventDefault();
          handler(e);
          return;
        }
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
